import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import { getCycle } from '@/api/cyclesService';
import { queryKeys } from '@/lib/queryKeys';
import type { SequenceRow, CycleMeta } from './planComposerTypes';

interface CycleLengthModalProps {
  row: SequenceRow | null;
  position: number;
  meta: CycleMeta | undefined;
  formatIdentifier: string;
  onClose: () => void;
  onApply: (uid: string, weekCount: number | null) => void;
  onRemove: (uid: string) => void;
}

const rangeLabel = (weeks: number[]): string => {
  const sorted = [...weeks].sort((a, b) => a - b);
  const groups: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (const p of sorted.slice(1)) {
    if (p === prev + 1) {
      prev = p;
      continue;
    }
    groups.push(start === prev ? `${start}` : `${start}–${prev}`);
    start = p;
    prev = p;
  }
  if (sorted.length > 0) groups.push(start === prev ? `${start}` : `${start}–${prev}`);
  return groups.join(', ');
};

const CycleLengthModal = ({ row, position, meta, formatIdentifier, onClose, onApply, onRemove }: CycleLengthModalProps) => {
  const { data: cycle } = useQuery({
    queryKey: queryKeys.pv2Cycle(row?.cycleId ?? ''),
    queryFn: () => getCycle(row!.cycleId),
    enabled: Boolean(row?.cycleId),
  });

  const minWeeks = meta?.minWeekNumber ?? 1;
  const maxWeeks = meta?.maxWeekNumber ?? 1;
  const capWeeks = row?.weekCount ?? null;

  const weekOptions = useMemo(
    () => Array.from({ length: Math.max(0, maxWeeks - minWeeks + 1) }, (_, i) => minWeeks + i),
    [minWeeks, maxWeeks]
  );

  const usage = useMemo(() => {
    if (!cycle) return null;
    const ranks = cycle.weekPriorities.filter((wp) => wp.formatIdentifier === formatIdentifier);
    const byRank = [...ranks].sort((a, b) => a.priority - b.priority);
    const effectiveMax = Math.min(maxWeeks, capWeeks ?? Infinity, ranks.length);
    const mandatory = byRank.slice(0, minWeeks).map((r) => r.weekNumber);
    const extra = byRank.slice(minWeeks, effectiveMax).map((r) => r.weekNumber);
    const beyondCap = byRank.slice(effectiveMax).map((r) => r.weekNumber);
    const unranked = Array.from({ length: maxWeeks }, (_, i) => i + 1).filter((w) => !ranks.some((r) => r.weekNumber === w));
    return { mandatory, extra, beyondCap, unranked, minRank: 1, extraFrom: minWeeks + 1, extraTo: effectiveMax };
  }, [cycle, formatIdentifier, minWeeks, maxWeeks, capWeeks]);

  return (
    <Modal open={Boolean(row)} onClose={onClose} title="Cycle dans ce plan" size="md">
      {row && (
        <>
          <div className="space-y-4 px-6 py-5">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              <span className="font-semibold">{meta?.name ?? 'Cycle'}</span> · Position {position} · {minWeeks}–{maxWeeks} semaines
            </p>

            <Select
              label="Plafond de semaines"
              value={capWeeks === null ? 'max' : String(capWeeks)}
              onChange={(e) => onApply(row.uid, e.target.value === 'max' ? null : Number(e.target.value))}
            >
              <option value="max">Maximum du cycle (défaut)</option>
              {weekOptions.map((w) => (
                <option key={w} value={w}>
                  {w} semaine(s)
                </option>
              ))}
            </Select>
            <p className="text-xs text-gray-400">
              Nombre maximal de semaines que ce cycle peut prendre dans ce plan. Le moteur en retire si l'athlète manque de temps.
            </p>

            {usage && (
              <div>
                <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  Semaines utilisées pour le format {formatIdentifier}
                </p>
                <ul className="space-y-0.5 text-xs">
                  {usage.mandatory.length > 0 && (
                    <li className="text-success-600 dark:text-success-400">
                      ✓ Semaines {rangeLabel(usage.mandatory)} — obligatoires (rang {usage.minRank}–{minWeeks})
                    </li>
                  )}
                  {usage.extra.length > 0 && (
                    <li className="text-gray-600 dark:text-gray-300">
                      ＋ Semaines {rangeLabel(usage.extra)} — ajoutées si le temps le permet (rang {usage.extraFrom}–{usage.extraTo})
                    </li>
                  )}
                  {usage.beyondCap.length > 0 && (
                    <li className="text-gray-400">— Semaines {rangeLabel(usage.beyondCap)} — au-delà du plafond</li>
                  )}
                  {usage.unranked.length > 0 && (
                    <li className="text-gray-400">— Semaines {rangeLabel(usage.unranked)} — non classées pour ce format</li>
                  )}
                </ul>
              </div>
            )}

            <p className="text-xs text-gray-400">Modifier le contenu des séances se fait dans le cycle.</p>
          </div>
          <div className="flex justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-800">
            <Button variant="danger" onClick={() => onRemove(row.uid)}>
              Retirer du plan
            </Button>
            <Button onClick={onClose}>Appliquer</Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default CycleLengthModal;
